import { useSearchParams } from 'react-router-dom'
import { compact, startCase } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useActivityDrawer } from 'app/hooks/useActivityDrawer'
import { useActivityOwnerUpdate } from 'app/hooks/useActivityOwnerUpdate'
import { useActivityReload } from 'app/hooks/useActivityReload'
import { useAddMarketingColumns } from 'app/hooks/useAddMarketingColumns'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useStickyLocationDefault } from 'app/hooks/useStickyLocation'
import { ActivityJson } from 'app/types/entities'
import { FilterObject, PredefinedFilter, encodeFilters, convertUrlFiltersToES } from 'app/filter.utils'
import type { SearchResponse } from 'app/services/search'
import { defaultColumns, getFilterableColumns } from 'app/table-columns/activities'
import { useUser, useWorkspace } from 'app/utils'
import { activitiesExclusionTerm } from 'lib/activitiesExclusion'
import { comActivityTypes } from 'lib/comActivityTypes'
import { useActivityStore } from 'app/stores/StoreContext'
import { useSavedSubpages } from 'app/hooks/useSavedSubpages'
import { usPhoneNumber } from 'lib/phone'

const activityTagPages: Record<string, string> = {
  'missed-call': `Missed Call`,
  callback: `Callback`,
  voicemail: `Voicemail`,
  'not-scheduled': `Appointment Not Scheduled`,
  canceled: `Canceled Appointment`,
  scheduled: `Appointment Scheduled`,
  rescheduled: `Appointment Rescheduled`,
  confirmation: `Appointment Confirmation`,
}

export type UseActivitiesPageReturn = {
  activities: SearchResponse<ActivityJson> | undefined
  setActivities: React.Dispatch<React.SetStateAction<SearchResponse<ActivityJson> | undefined>>
  columns: any
  memoizedFilters: any
  isLoading: boolean
  initialSort: any
  drawer: ReturnType<typeof useActivityDrawer>
  onOwnerUpdate: ReturnType<typeof useActivityOwnerUpdate>
  loadActivity: ReturnType<typeof useActivityReload>
  data: {
    tab: string
    joiner: `and` | `or` | undefined
    filters: PredefinedFilter
    initialFilters: FilterObject[] | undefined
    subpages: Array<{ id: string; name: string; filters: FilterObject[]; join: string }>
    activeSubpage: string
    sort: string | undefined
    commActivityTypes: typeof comActivityTypes
  }
  pageTitle: string
  searchTerms: string
}

export function useActivitiesPage(): UseActivitiesPageReturn {
  const [searchParams] = useSearchParams()
  const addMarketingColumns = useAddMarketingColumns()
  const workspace = useWorkspace()
  const user = useUser()

  const tab = searchParams.get(`tab`) || `all`
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `activityDate:desc`)
  const page = Number(searchParams.get(`page`) || 0)
  const query = searchParams.get(`query`) ?? ``
  const join = searchParams.get(`join`)
  const limit = (Number(searchParams.get(`limit`)) as 0 | 1 | 100 | 1000) || undefined

  // Build filters client-side based on tab
  let filters: PredefinedFilter = {}
  let joiner: `and` | `or` | undefined
  const { subpages, loaded: subpagesLoaded } = useSavedSubpages(`activities`)

  if (tab !== `all`) {
    const subpage = subpages?.find((s) => s.id === tab)
    if (subpage) {
      joiner = subpage.join as `and` | `or`
      filters = subpage.filters.reduce(
        (acc, filter) => ({ ...acc, [filter.attribute]: { value: filter.value, operator: filter.operator } }),
        {},
      )
    } else if (activityTagPages[tab]) {
      filters = { tags: { value: [activityTagPages[tab]], operator: `in` } }
    }
  }
  // The per-device location rides along as one more tab default, so the chip
  // shows and any later filter edit writes it into the URL with the rest.
  const stickyLocation = useStickyLocationDefault()
  if (stickyLocation.length) {
    filters = { ...filters, locationId: { value: stickyLocation, operator: `in` } }
  }

  const data = {
    commActivityTypes: comActivityTypes,
    tab,
    joiner,
    filters,
    initialFilters: subpages.find((s) => s.id === tab)?.filters,
    subpages,
    activeSubpage: startCase(subpages.find((s) => s.id === tab)?.name ?? tab),
    sort,
  }

  const stickyLocationKey = stickyLocation.join(`,`)
  const memoizedFilters = useMemo(
    () =>
      getFilterableColumns({
        comActivityTypes: comActivityTypes,
        preAppliedFilters: filters,
        addMarketingColumns,
        addFollowUp: tab === `all` || tab === ``,
      }),
    [workspace, tab, stickyLocationKey],
  )
  const initialSort = useInitialTableSort(sort)

  const [activities, setActivities] = useState<SearchResponse<ActivityJson>>()
  const activityStore = useActivityStore()
  const isLoading = false
  const drawer = useActivityDrawer(`activities`)
  const onOwnerUpdate = useActivityOwnerUpdate(activities, setActivities)
  const loadActivity = useActivityReload(drawer.setFocusedActivity, setActivities)
  const activityRequestIdRef = useRef(0)
  const prevActivitySearchKeyRef = useRef<string>(``)

  // Fetch activities via store
  const filtersParam = searchParams.get(`filters`)
  // `filters=` means the user cleared their own filters, so the remembered
  // location stays gone (useStickyLocationDefault reads the key, not the
  // value). The tag that defines the tab is not theirs to clear though:
  // Missed Call without it is every activity. Read the value, not the key.
  const hasFilters = !!searchParams.get(`filters`)
  const defaultActivitiesTerm = activitiesExclusionTerm(user)
  const queryDigits = usPhoneNumber(query)
  const searchQueryTerm = queryDigits
    ? `(contactPhone:${queryDigits} OR phoneFromActivity:${queryDigits} OR contacts.phone:${queryDigits})`
    : query

  const searchTerms = useMemo(() => {
    const predefinedFilterTerms = encodeFilters(
      Object.entries(filters).map(([attribute, f]) => ({ attribute, ...f })) as FilterObject[],
    )
    // Filters in the URL carry their own Match any / Match all; only a saved list's joiner covers the rest.
    const urlJoiner = join === `and` || join === `or` ? join : joiner
    const urlFilterTerms = convertUrlFiltersToES(filtersParam, urlJoiner)
    const pageFilterTerms = hasFilters ? [] : convertUrlFiltersToES(predefinedFilterTerms, joiner)
    return compact([defaultActivitiesTerm, searchQueryTerm, ...urlFilterTerms, ...pageFilterTerms]).join(` `)
  }, [defaultActivitiesTerm, searchQueryTerm, filtersParam, hasFilters, filters, joiner, join])

  // `subpages` is rebuilt with .map() on every render, so using the array itself
  // as an effect dependency refired the activities query on EVERY render — including
  // the re-render that follows creating a Staff member inline from the Staff column.
  // Each refire replaced the list with whatever the server returned at that moment,
  // discarding the optimistic owner update and the user's current view. Key on the ids
  // instead: a string compares by value, and a subpage whose filters changed already
  // moves `searchTerms`, which is a dependency in its own right.
  const subpagesKey = subpages.map((s) => s.id).join(`,`)
  const activityListKey = `${tab}|${page}|${query}|${join ?? ``}|${filtersParam ?? ``}|${limit ?? ``}|${sort ?? ``}|${searchTerms}`
  const activitySearchKey = `${tab}|${page}|${query}|${join ?? ``}|${filtersParam ?? ``}|${limit ?? ``}`

  const waitingForSavedList = tab !== `all` && !activityTagPages[tab] && !subpagesLoaded
  useEffect(() => {
    if (waitingForSavedList) return
    const requestId = activityRequestIdRef.current + 1
    activityRequestIdRef.current = requestId
    const [field, direction] = sort?.split(`:`) ?? []
    // Only clear activities when search criteria change (not on sort-only or
    // drawer-only URL changes). The drawer is an overlay on the current list.
    if (activitySearchKey !== prevActivitySearchKeyRef.current) {
      setActivities(undefined)
      prevActivitySearchKeyRef.current = activitySearchKey
    }
    activityStore
      .fetchList({
        search: searchTerms || undefined,
        pagination: { page, limit: limit ?? undefined },
        sort: field ? { field, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'DESC' } : undefined,
      })
      .then((result) => {
        if (requestId !== activityRequestIdRef.current) return
        const hits = (result.data as any)?.activities
        if (hits) setActivities(hits)
      })
  }, [activityListKey, activitySearchKey, subpagesKey, waitingForSavedList])

  // `addMarketingColumns` starts false and flips to true once the user and feature toggles load.
  // It must be a dependency: without it the memo keeps the column set it computed on first render,
  // so the table shows a different set depending on whether that first render ran before or after
  // the flip. A fresh URL load (data not cached) and an in-app click (data cached) then land on
  // different column sets for the same page — e.g. the Caller ID column appears one way but not the
  // other. (selectActivity and onOwnerUpdate are recreated each render, so keep the stable
  // focusedActivity proxy that stands in for a selection change rather than adding those refs.)
  const columns = useMemo(
    () => defaultColumns(drawer.selectActivity, addMarketingColumns, onOwnerUpdate, drawer.baseUrl),
    [drawer.focusedActivity, drawer.baseUrl, addMarketingColumns],
  )

  const pageTitle = useMemo(() => {
    if (tab === `all`) return `Activities`
    if (tab === `insurance-state-hmo`) return `Insurance: State / HMO`
    return startCase(data.activeSubpage as string)
  }, [tab, data.activeSubpage])

  return {
    activities,
    setActivities,
    columns,
    memoizedFilters,
    isLoading,
    initialSort,
    drawer,
    onOwnerUpdate,
    loadActivity,
    data,
    pageTitle,
    searchTerms,
  }
}
